import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n';
import InfoTooltip from './InfoTooltip';

// Legal & cookie consent (iubenda) — the three hosted policy documents.
//
// iubenda hosts and versions these pages; we only link to them. The "direct
// link" integration is deliberate: iubenda also ships an `iubenda.js` embed
// script that rewrites <a> tags in place after load, which fights React's
// reconciliation (it mutates nodes React owns and re-runs on every remount).
// Plain anchors to the hosted URLs are the same legal artefact with none of
// that, so the URLs live here as constants and nothing mutates the DOM.
//
// Opened in a new tab so a user reading the Terms mid-signup does not lose the
// form they were filling in; `rel="noopener noreferrer"` because `target=_blank`
// otherwise hands the opened page a live `window.opener` handle.
export const LEGAL_URLS = {
    privacy: 'https://www.iubenda.com/privacy-policy/90845158',
    cookies: 'https://www.iubenda.com/privacy-policy/90845158/cookie-policy',
    terms: 'https://www.iubenda.com/terms-and-conditions/90845158',
};

// The one document PAI hosts itself. The path is a requirement, not a
// preference: it must contain both "legal" and "PAI", because the iubenda Terms
// incorporate it by reference at this exact URL ("Advanced incorporation of
// other documents" → Custom Statements). Changing it breaks that incorporation
// on iubenda's side, which is not in this repo — so it is a constant, here,
// once.
export const ADDENDUM_PATH = '/legal/pai-data-ai-addendum';

// The legal entity behind PAI and its one support address (#461). The entity
// name, postal address and email are LITERAL in every language — they are
// identifiers, not prose — so only the surrounding chrome is translated.
export const LEGAL_ENTITY = 'The Title AI LLC';
export const LEGAL_ENTITY_ADDRESS = '30 N Gould St Ste R, Sheridan, WY 82801, USA';
export const SUPPORT_EMAIL = 'support@workbypai.com';
// The spec fixes the year (#461: "© 2026 The Title AI LLC. All rights
// reserved."), so it is a literal, not `new Date().getFullYear()`.
export const COPYRIGHT_YEAR = '2026';

// The DMCA takedown-notice page, also hosted by us. Fixed at /dmca because
// that is the path referenced by our U.S. Copyright Office DMCA agent filing —
// not this repo's choice to make.
export const DMCA_PATH = '/dmca';

// The one place a legal link's chrome is defined, so the footer, the signup
// consent sentence and the Settings section can never drift apart.
export const LEGAL_LINK_CLASS =
    'text-pai-meta hover:text-white underline underline-offset-2 decoration-white/25 hover:decoration-white/60 transition-colors focus:outline-none focus-visible:text-white';

export const LegalLink = ({ href, testId, children, className = '', onClick }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        onClick={onClick}
        className={`${LEGAL_LINK_CLASS} ${className}`.trim()}
    >
        {children}
    </a>
);

// The one document we host ourselves, so it is an in-app navigation rather than
// a new tab: there is nothing to lose by leaving the page, and the reader stays
// inside PAI.
export const AddendumLink = ({ testId = 'legal-link-addendum', className = '', children }) => (
    <Link
        to={ADDENDUM_PATH}
        data-testid={testId}
        className={`${LEGAL_LINK_CLASS} ${className}`.trim()}
    >
        {children}
    </Link>
);

// The DMCA notice page — likewise hosted by us, so an in-app navigation.
export const DmcaLink = ({ testId = 'legal-link-dmca', className = '', children }) => (
    <Link
        to={DMCA_PATH}
        data-testid={testId}
        className={`${LEGAL_LINK_CLASS} ${className}`.trim()}
    >
        {children}
    </Link>
);

// Inside a <label>, a click anywhere in the label is forwarded to the control it
// labels — including a click on a link, in some engines. Reading the Terms must
// never tick the box that says you accept them, so the anchors stop the event
// before it reaches the label.
const stopLabelActivation = (e) => e.stopPropagation();

// ─────────────────────────────────────────────────────────────────────────────
// Re-opening the cookie preferences panel (Iubenda bug #401).
//
// iubenda can render a PERSISTENT floating badge that re-opens this panel; it
// is the thing the card asks us to remove, and it is now switched off on the
// iubenda side ("Privacy widget → Add manually"). Turning it off without
// offering another way in would leave a user unable to change their mind, which
// is itself a compliance problem — so the re-opener moves into User Settings,
// which is exactly where iubenda's own "add manually" guidance points ("an
// easily accessible spot, for example, the footer or the application settings").
//
// iubenda's manual snippet is an <a class="iubenda-cs-preferences-link">, which
// their script finds and binds by DOM mutation. We deliberately do NOT use that
// class — it is the same reason the policy links here are plain anchors and not
// iubenda's `iubenda.js` embed: a script that rewrites nodes React owns fights
// reconciliation. Calling the documented API from an onClick is the same
// behaviour with none of that.
const prefsApi = () => {
    const api = typeof window !== 'undefined' && window._iub && window._iub.cs && window._iub.cs.api;
    return api && typeof api.openPreferences === 'function' ? api : null;
};

// How long to keep looking for the widget before giving up. It is fetched from
// iubenda's CDN, so it is not there on first paint — but it must not be polled
// forever either (blocked by an ad-blocker, offline, or a failed CDN fetch are
// all real and all permanent for this page load).
const PREFS_POLL_MS = 400;
const PREFS_POLL_LIMIT = 40; // ~16s

export const useCookiePreferences = () => {
    const [available, setAvailable] = useState(() => !!prefsApi());

    useEffect(() => {
        if (available) return undefined;
        let tries = 0;
        const id = setInterval(() => {
            tries += 1;
            if (prefsApi()) {
                setAvailable(true);
                clearInterval(id);
            } else if (tries >= PREFS_POLL_LIMIT) {
                clearInterval(id);
            }
        }, PREFS_POLL_MS);
        return () => clearInterval(id);
    }, [available]);

    // Re-checked at call time rather than trusted from state: the widget can go
    // away between render and click, and opening a panel must never throw into
    // a click handler.
    const open = useCallback(() => {
        const api = prefsApi();
        if (api) api.openPreferences();
    }, []);

    return { available, open };
};

/**
 * The click-wrap line under a "Continue with Google" button.
 *
 * An OAuth button creates an account in one tap — there is no form to attach a
 * checkbox to, and interposing one would break the single-tap flow the provider
 * is chosen for — so the terms are stated at the point of the click instead.
 *
 * ⚠️ The ENGLISH WORDING IS PROOF TEXT: the backend records what a user was
 * shown, keyed on the sign-in provider. Change these strings only together with
 * that record.
 */
export const GoogleConsentNotice = ({ className = '' }) => {
    const { t } = useLanguage();

    return (
        <p
            data-testid="google-legal-consent"
            className={`text-[12.5px] leading-relaxed text-pai-meta ${className}`.trim()}
        >
            {t('authGoogleConsentPrefix', 'By continuing with Google you agree to the')}{' '}
            <LegalLink href={LEGAL_URLS.terms} testId="google-legal-terms">
                {t('legalTerms', 'Terms and Conditions')}
            </LegalLink>{' '}
            {t('authGoogleConsentMiddle', 'and acknowledge the')}{' '}
            <LegalLink href={LEGAL_URLS.privacy} testId="google-legal-privacy">
                {t('legalPrivacyPolicy', 'Privacy Policy')}
            </LegalLink>
            {t('authGoogleConsentSuffix', '.')}
        </p>
    );
};

/**
 * The signup consent CHECKBOX.
 *
 * Unchecked by default and never pre-ticked — a pre-ticked box is not consent,
 * and in the EU it is explicitly not consent. The caller owns the state because
 * the same value also gates its submit button.
 *
 * Extra terms and 18 (#410): the sentence ALSO states an explicit age
 * confirmation, as its own trailing sentence (`authLegalConsentAge`) rather
 * than folded into the Terms/Privacy clause — it is a separate assertion
 * ("I am 18+"), not a third document to acknowledge. ⚠️ The assembled English
 * text is PROOF TEXT: `BE/app/services/consent_service.py`'s
 * `CONSENT_PROOF_PASSWORD_CHECKBOX` mirrors it byte-for-byte, so change both
 * together (and the BE's literal-quoted pinned test).
 */
export const SignupConsentCheckbox = ({ checked, onChange, className = '' }) => {
    const { t } = useLanguage();

    return (
        <div
            data-testid="signup-legal-consent"
            className={`flex items-start gap-3 ${className}`.trim()}
        >
            <input
                id="legal-consent"
                type="checkbox"
                data-testid="signup-legal-checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-[2px] h-4 w-4 flex-shrink-0 cursor-pointer rounded-[4px] border border-pai-strong bg-white/[0.06] accent-white focus:outline-none focus-visible:border-white/80"
            />
            <label
                htmlFor="legal-consent"
                className="cursor-pointer text-[12.5px] leading-relaxed text-pai-meta"
            >
                {t('authLegalConsentPrefix', 'I agree to the')}{' '}
                <LegalLink
                    href={LEGAL_URLS.terms}
                    testId="signup-legal-terms"
                    onClick={stopLabelActivation}
                >
                    {t('legalTerms', 'Terms and Conditions')}
                </LegalLink>{' '}
                {t('authLegalConsentMiddle', 'and acknowledge the')}{' '}
                <LegalLink
                    href={LEGAL_URLS.privacy}
                    testId="signup-legal-privacy"
                    onClick={stopLabelActivation}
                >
                    {t('legalPrivacyPolicy', 'Privacy Policy')}
                </LegalLink>
                {t('authLegalConsentSuffix', '.')}{' '}
                <span data-testid="signup-legal-age">
                    {t('authLegalConsentAge', 'I confirm that I am 18 years of age or older.')}
                </span>
            </label>
        </div>
    );
};

/**
 * The Privacy Policy · Cookie Policy · Terms · Data & AI Addendum · DMCA row.
 *
 * The first three are hosted by iubenda and open in a new tab; the last two
 * are ours and navigate in place.
 *
 * Mounted on every unauthenticated page (login, signup, forgot-password, the two
 * onboarding gates), on the two public legal pages, and inside User Settings —
 * i.e. everywhere a user can be without already having found the app's chrome.
 * The copyright line (#461) rides underneath, so the same mounts carry it.
 * `align` ('center' | 'end') aligns both the row and the copyright line.
 */
/**
 * The copyright line (#461): "© 2026 The Title AI LLC. All rights reserved."
 *
 * The entity name is a button that opens a click-toggled popover naming the
 * company behind PAI, its registered address and the support mailbox. It reuses
 * InfoTooltip's click-only mode, so it inherits the portal-to-<body>, the
 * outside-press dismissal and the CAPTURED Escape (closing it never closes the
 * Settings modal it may sit inside — the #272/#390 rule).
 */
export const LegalCopyright = ({ className = '' }) => {
    const { t } = useLanguage();

    const bubble = (
        <span data-testid="legal-entity-popover-body" className="block space-y-1">
            <span data-testid="legal-entity-product-of" className="block text-white">
                {t('legalEntityProductOf', 'PAI is a product of')} {LEGAL_ENTITY}
            </span>
            <span data-testid="legal-entity-address" className="block">
                {LEGAL_ENTITY_ADDRESS}
            </span>
            <span data-testid="legal-entity-email" className="block">
                {t('legalEntityEmailLabel', 'Email:')}{' '}
                <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    data-testid="legal-entity-email-link"
                    className="text-pai-blue underline underline-offset-2 hover:text-pai-blue-tint"
                >
                    {SUPPORT_EMAIL}
                </a>
            </span>
        </span>
    );

    return (
        <p
            data-testid="legal-copyright"
            className={`text-[12px] leading-relaxed text-pai-faint ${className}`.trim()}
        >
            © {COPYRIGHT_YEAR}{' '}
            <InfoTooltip
                testId="legal-entity-button"
                bubbleTestId="legal-entity-popover"
                bubbleRole="dialog"
                hoverable={false}
                label={LEGAL_ENTITY}
                text={bubble}
                trigger={LEGAL_ENTITY}
                className={`${LEGAL_LINK_CLASS} cursor-pointer bg-transparent p-0 text-[12px]`}
            />
            . {t('legalAllRightsReserved', 'All rights reserved.')}
        </p>
    );
};

const ALIGN_ROW = { center: 'justify-center', end: 'justify-end' };
const ALIGN_TEXT = { center: 'text-center', end: 'text-right' };

const LegalLinks = ({ className = '', align = 'center' }) => {
    const { t } = useLanguage();

    // touch:gap-6 (#479 audit): at 390px the wrapped link row sat 16px above the
    // copyright line, so a tap 19px below "Data & AI Addendum" hit the entity
    // button — now the same 24px gap as between wrapped link rows.
    return (
        <div className={`flex flex-col gap-2 touch:gap-6 ${className}`.trim()}>
        <div
            data-testid="legal-links"
            className={`flex flex-wrap items-center ${ALIGN_ROW[align] || ALIGN_ROW.center} gap-x-2 gap-y-1 touch:gap-y-6 text-[12.5px]`}
        >
            <LegalLink href={LEGAL_URLS.privacy} testId="legal-link-privacy">
                {t('legalPrivacyPolicy', 'Privacy Policy')}
            </LegalLink>
            <span aria-hidden="true" className="text-pai-faint">·</span>
            <LegalLink href={LEGAL_URLS.cookies} testId="legal-link-cookies">
                {t('legalCookiePolicy', 'Cookie Policy')}
            </LegalLink>
            <span aria-hidden="true" className="text-pai-faint">·</span>
            <LegalLink href={LEGAL_URLS.terms} testId="legal-link-terms">
                {t('legalTerms', 'Terms and Conditions')}
            </LegalLink>
            <span aria-hidden="true" className="text-pai-faint">·</span>
            {/* Hosted by us, not iubenda — and incorporated into the Terms
                above, so it belongs beside them rather than a level down. */}
            <AddendumLink>
                {t('legalAddendum', 'Data & AI Addendum')}
            </AddendumLink>
            <span aria-hidden="true" className="text-pai-faint">·</span>
            {/* Also hosted by us — the takedown-notice page our DMCA agent
                filing points at. */}
            <DmcaLink>
                {t('legalDmca', 'DMCA')}
            </DmcaLink>
        </div>
        <LegalCopyright className={ALIGN_TEXT[align] || ALIGN_TEXT.center} />
        </div>
    );
};

export default LegalLinks;
